import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import queryClient from '~/queryClient';
import {
  changeSlackChannel,
  getSlackChannel,
  getSlackChannelInfo,
  linkToSlack,
  listSlackChannels,
  NotificationType,
  removeSlackChannel,
  unlinkFromSlack,
  SlackChannelResponse,
} from '~/services/slackAPI';
import { useLazyQuery } from '../useLazyQuery';
import {
  formatErrorPayload,
  UseApiMutationOptions,
  UseApiQueryOptions,
  validateApiResponse,
} from '../utils';
import { useCurrentWorkspaceQueryKey } from '../utils/useQueryKey';

const ROOT_KEY = 'slack';
const getRootQueryKey = () => [ROOT_KEY];
const getQueryKey = (channelId: string) => [ROOT_KEY, channelId];
const getProcessQueryKey = (processId: string) => [ROOT_KEY, 'process', processId];

export const useSlackChannelsQuery = (options?: UseApiQueryOptions<typeof listSlackChannels>) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(getRootQueryKey()),
    async () => validateApiResponse(await listSlackChannels({ workspaceId })).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useSlackChannelQuery = (
  channelId: string,
  options?: UseApiQueryOptions<typeof getSlackChannelInfo>,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(getQueryKey(channelId)),
    async () => validateApiResponse(await listSlackChannels({ workspaceId })).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useLinkToSlackMutation = <T extends typeof linkToSlack>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (args: { state: string; code: string }) =>
      validateApiResponse(await linkToSlack({ workspaceId, link: args })).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useUnlinkToSlackMutation = <T extends typeof unlinkFromSlack>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(async () => validateApiResponse(await unlinkFromSlack({ workspaceId })).data, {
    onError: (error) => dispatch(formatErrorPayload(error)),
    ...options,
  });
};

export const useProcessSlackChannelQuery = (
  processId: string,
  options?: UseApiQueryOptions<typeof getSlackChannel>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  const queryKey = getProcessQueryKey(processId);
  return useQuery(
    queryKey,
    async () => validateApiResponse(await getSlackChannel({ workspaceId, processId })).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useProcessSlackChannelLazyQuery = () => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useLazyQuery<{ processId: string }, SlackChannelResponse | undefined, Error>(
    ({ processId }) => getProcessQueryKey(processId),
    ({ processId }) =>
      async () =>
        validateApiResponse(await getSlackChannel({ workspaceId, processId })).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
    },
  );
};

export const useChangeProcessSlackChannelMutation = <T extends typeof changeSlackChannel>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      processId,
      ...args
    }: {
      processId: string;
      successfulRuns?: Array<{ channelId: string }>;
      failedRuns?: Array<{ channelId: string }>;
    }) =>
      validateApiResponse(await changeSlackChannel({ workspaceId, channels: args, processId }))
        .data,
    {
      onSuccess: (data, { processId }) => {
        queryClient.setQueryData(getProcessQueryKey(processId), data);
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useDeleteProcessSlackChannelMutation = <T extends typeof removeSlackChannel>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (args: { processId: string; channelId: string; notificationType: NotificationType }) =>
      validateApiResponse(await removeSlackChannel({ workspaceId, params: args })).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};
